<template>
    <div
        class="hidden md:block lg:flex lg:flex-wrap lg:items-center lg:justify-between bg-white px-6 py-3 border-b border-gray-200 space-y-5 lg:space-y-0 lg:space-x-4"
    >
        <div class="flex-1 min-w-0">
            <!-- Title. -->
            <h2 class="text-xl font-bold leading-7 text-gray-900 sm:text-2xl sm:truncate align-middle">
                {{ packageItem.project }} • {{ packageItem.name }}
                <a :href="'mailto:?subject='+packageItem.reference+' - '+packageItem.project+' '+packageItem.name"
                   class="opacity-25 hover:opacity-100 font-medium pl-1.5">
                    <i class="align-top mt-2.5 text-sm far fa-envelope w-4 text-blue-400"></i>
                </a>
            </h2>
            <!-- Metadata. -->
            <div class="mt-2 flex flex-col sm:flex-row sm:flex-wrap sm:space-x-6 z-30">
                <div class="flex items-center text-xs text-gray-500" v-if="showLegacy">
                    <i class="fas fa-user-tie fa-fw text-gray-400 flex-shrink-0 text-sm mr-1.5"></i>
                    {{
                        packageItem.client ? packageItem.client.name : 'Client Not Found'
                    }}
                </div>
                <ui-dropdown menu-class="p-3" style="z-index: 101">
                    <template #default="{ open, close, toggle }">
                        <div class="flex items-center text-xs text-gray-500 cursor-pointer"
                             @click="toggle"
                        >
                            <i class="fas fa-map fa-fw text-gray-400 flex-shrink-0 text-sm mr-1.5"></i>
                            {{ packageItem.address }}<span
                            v-if="packageItem.postcode">&nbsp;-&nbsp;{{ packageItem.postcode }}</span>
                            <i class="far fa-clone text-blue-400 opacity-25 pl-1.5 hover:opacity-100"
                               @click.stop="clipboard(packageItem.address+(packageItem.postcode?' - '+packageItem.postcode:''))"></i>
                        </div>
                    </template>
                    <template #menu="{ close }">
                        <div class="w-[36rem] h-96">
                            <iframe width="100%" height="100%" frameborder="0"
                                    :src="'https://www.google.com/maps/embed/v1/place?key='+googleKey+'&q='+packageItem.postcode.replace(' ','+')">
                            </iframe>
                        </div>
                    </template>
                </ui-dropdown>
                <ui-dropdown menu-class="p-3 w-52" v-if="showLegacy">
                    <template #default="{ open, close }">
                        <div
                            class="flex items-center text-xs text-gray-500"
                            @mouseenter="open"
                            @mouseleave="close"
                        >
                            <i class="fas fa-calendar fa-fw text-gray-400 flex-shrink-0 text-sm mr-1.5"></i>
                            Created {{ createdAt }}
                        </div>
                    </template>
                    <template #menu>
                        <div class="text-sm text-gray-700">
                            {{ createdAgo }}
                        </div>
                    </template>
                </ui-dropdown>
                <ui-dropdown menu-class="p-3 w-52" v-if="showLegacy">
                    <template #default="{ open, close }">
                        <div
                            class="flex items-center text-xs text-gray-500"
                            @mouseenter="open"
                            @mouseleave="close"
                        >
                            <i class="fas fa-calendar-edit fa-fw text-gray-400 flex-shrink-0 text-sm mr-1.5"></i>
                            Last updated {{ updatedAgo }}
                        </div>
                    </template>
                    <template #menu>
                        <div class="text-sm text-gray-700">
                            {{ updatedAt }}
                        </div>
                    </template>
                </ui-dropdown>
            </div>
        </div>
        <div class="flex-shrink-0 flex space-x-2">
            <!-- Flags. -->
            <button
                class="flex space-x-4 hover:bg-gray-100 rounded-lg px-4 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                @click="$modals.open('package-update-flag-modal', { packageItem })"
                v-if="showLegacy"
            >
                <div
                    v-for="flag in filteredFlags"
                    :key="flag.value"
                    class="flex flex-col items-center justify-end h-full"
                >
                    <i class="fa-fw text-gray-400 text-base mb-1" :class="flag.icon"></i>
                    <div
                        v-text="flag.label"
                        class="text-xs font-medium text-gray-500"
                    ></div>
                </div>
                <div
                    v-if="filteredFlags.length === 0"
                    class="flex flex-col items-center justify-end h-full"
                >
                    <i class="fa-fw far fa-flag text-gray-400 text-base mb-1"></i>
                    <div class="text-xs font-medium text-gray-500">No flags</div>
                </div>
            </button>

            <!-- Linked status. -->
            <button
                class="flex flex-col items-center justify-end hover:bg-gray-100 rounded-lg px-4 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                @click="$modals.open('package-update-linked-modal', { packageItem })"
                v-if="showLegacy"
            >
                <i
                    class="fas fa-fw text-gray-400 text-base mb-1"
                    :class="(packageItem.meta.linked) ? 'fa-link' : 'fa-unlink'"
                ></i>
                <div
                    class="text-xs font-medium text-gray-500"
                    v-text="(packageItem.meta.linked) ? 'Linked' : 'Not linked'"
                ></div>
            </button>

            <!-- Status. -->
            <button
                class="flex flex-col items-center justify-end hover:bg-gray-100 rounded-lg px-4 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                @click="$modals.open('package-update-status-modal', { packageItem })"
                v-if="showLegacy"
            >
                <div
                    class="text-sm font-medium mb-0.5"
                    v-text="packageItem.status.name"
                ></div>
                <div
                    v-if="packageItem.status.id !== 600"
                    class="text-xs font-medium text-gray-500"
                >
                    Status
                </div>
                <div v-else class="text-xs font-medium text-gray-500">
                    {{ packageItem.progress }}%
                </div>
            </button>

            <!-- ID. -->
            <div
                class="flex flex-col items-center justify-end bg-gray-50 border border-gray-200 rounded-lg px-4 py-1.5">
                <div
                    class="text-base font-semibold"
                    v-text="packageItem.reference"
                ></div>
                <div class="text-xs font-medium text-gray-500">Reference</div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import {Package} from '@models'
import UiDropdown from "../ui/UiDropdown.vue";

export default {
    components: {UiDropdown},
    props: {
        package: Object,
        googleKey: '',
        showLegacy: {type: Boolean, default: true},
    },
    data() {
        return {
            flags: Package.flags(),
            packageItem: Package.make(this.package),
        }
    },
    computed: {
        filteredFlags() {
            return this.flags.filter((flag) => this.packageItem.meta[flag.value])
        },
        createdAt() {
            return moment(this.packageItem.created_at).format('llll')
        },
        createdAgo() {
            return moment(this.packageItem.created_at).fromNow()
        },
        updatedAt() {
            return moment(this.packageItem.updated_at).format('llll')
        },
        updatedAgo() {
            return moment(this.packageItem.updated_at).fromNow()
        },
    },
    methods: {
        clipboard(text) {
            navigator.clipboard.writeText(text);
        },
        route,
    },
    mounted() {
    }
}
</script>
